import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import moment from "moment";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faXmark } from "@fortawesome/pro-regular-svg-icons";
import {
  AccountBatchJobsDrawerFilters,
  countActiveAccountBatchJobsFilters,
} from "./AccountBatchJobsFilterDrawer";
import "./accountsAppliedFilters.scss";

export type AccountBatchJobsAppliedFilterKey = "date" | "job_status" | "status";

type Chip = {
  key: AccountBatchJobsAppliedFilterKey;
  label: string;
};

type Props = {
  filters: AccountBatchJobsDrawerFilters;
  onRemoveFilter: (key: AccountBatchJobsAppliedFilterKey) => void;
  onClearAll: () => void;
};

function statusLabel(value: string, t: (key: string) => string): string {
  const labels: Record<string, string> = {
    draft: t("Draft"),
    scheduled: t("Scheduled"),
    pending: t("Pending"),
    processing: t("Processing"),
    complete: t("Complete"),
    partial: t("Partial"),
    failed: t("Failed"),
    success: t("Succeeded"),
  };
  return labels[value] ?? value;
}

export default function AccountBatchJobsAppliedFilters(props: Props) {
  const { filters, onRemoveFilter, onClearAll } = props;
  const { t } = useTranslation();

  const chips = useMemo(() => {
    const next: Chip[] = [];

    if (filters.useDateFilter) {
      next.push({
        key: "date",
        label: `${t("Created Date")}: ${moment(filters.filterDate).format("MMM D, YYYY")}`,
      });
    }

    if (filters.jobStatusFilter) {
      next.push({
        key: "job_status",
        label: `${t("Batch Status")}: ${statusLabel(filters.jobStatusFilter, t)}`,
      });
    }

    if (filters.statusFilter) {
      next.push({
        key: "status",
        label: `${t("Account Result")}: ${statusLabel(filters.statusFilter, t)}`,
      });
    }

    return next;
  }, [filters, t]);

  if (countActiveAccountBatchJobsFilters(filters) === 0) {
    return null;
  }

  return (
    <div className="accounts-applied-filters">
      <div className="accounts-applied-filters__chips">
        {chips.map(chip => (
          <div key={chip.key} className="accounts-applied-filters__chip">
            <span>{chip.label}</span>
            <button
              type="button"
              className="accounts-applied-filters__chip-remove"
              aria-label={t("Remove filter")}
              onClick={() => onRemoveFilter(chip.key)}
            >
              <FontAwesomeIcon icon={faXmark} />
            </button>
          </div>
        ))}
        <button type="button" className="accounts-applied-filters__clear-all" onClick={onClearAll}>
          <FontAwesomeIcon icon={faXmark} />
          {t("Clear Filters")}
        </button>
      </div>
    </div>
  );
}
